import type { ReactNode } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { proCourses, getCourse, firstSentence, shortSalary } from '@/content/proCourses';
import { RangeBars } from '@/components/RangeBars';

function Pill({ children }: { children: ReactNode }) {
  return <span className="aces-pill px-3 py-1 text-xs">{children}</span>;
}

function SectionHead({ kicker, title }: { kicker: string; title: string }) {
  return (
    <div className="mb-5">
      <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-accent-600 mb-2">{kicker}</span>
      <h2 className="font-serif text-2xl md:text-[1.7rem] text-ink-900 tracking-tight">{title}</h2>
    </div>
  );
}

/**
 * /careers/courses/:slug — one professional-credential deep dive.
 * Renders market, salary (India + abroad), where-you-work, growth, an honest
 * India-recognition note, pros/cons, and an optional explainer video.
 */
export function CourseDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const course = slug ? getCourse(slug) : undefined;
  if (!course) return <Navigate to="/careers/courses/" replace />;

  const url = `https://acescommerce.com/careers/courses/${course.slug}/`;
  const others = proCourses.filter((c) => c.slug !== course.slug);

  return (
    <div>
      <SEO
        title={`${course.name} — Scope, Salary, Eligibility & Career in India | ACES`}
        description={`${course.oneLiner} Starting salary in India: ${course.startingSalaryIndia}. Eligibility, cost, exam structure, where you can work, and career growth — explained for commerce students.`}
        canonical={url}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Course',
          name: `${course.fullName} (${course.name})`,
          description: course.overview,
          provider: { '@type': 'Organization', name: course.awardingBody },
          inLanguage: 'en-IN',
        }}
      />

      {/* Breadcrumb */}
      <div className="bg-cream/70 border-b border-ink-200/80">
        <nav className="max-w-4xl mx-auto px-4 py-3 text-xs text-ink-500 flex flex-wrap items-center gap-1.5">
          <Link to="/" className="hover:text-accent-600">Home</Link><span className="text-ink-300">·</span>
          <Link to="/careers/" className="hover:text-accent-600">Careers</Link><span className="text-ink-300">·</span>
          <Link to="/careers/courses/" className="hover:text-accent-600">Professional courses</Link><span className="text-ink-300">·</span>
          <span className="text-ink-700 font-medium">{course.name}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="aces-hero">
        <div className="max-w-4xl mx-auto px-4 pt-12 sm:pt-14 pb-12">
          <h1 className="font-serif text-[2.6rem] sm:text-6xl text-ink-900 tracking-tight leading-[1.04]">{course.name}</h1>
          <p className="mt-2 text-ink-500 text-sm">{course.fullName} · {course.awardingBody}</p>
          <p className="mt-5 text-lg sm:text-[1.2rem] text-ink-700 max-w-2xl leading-relaxed text-pretty">{course.oneLiner}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            <Pill>{firstSentence(course.duration)}</Pill>
            <Pill>{firstSentence(course.costInr)}</Pill>
          </div>
        </div>
      </section>

      {/* Video (optional) */}
      {course.video?.youtubeId && (
        <section className="bg-cream border-y border-ink-200">
          <div className="max-w-3xl mx-auto px-4 py-10">
            <SectionHead kicker="Watch first" title={`${course.name} in a few minutes`} />
            <div
              className="relative w-full overflow-hidden rounded-card border border-ink-200 shadow-card"
              style={{ aspectRatio: '16 / 9' }}
            >
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube-nocookie.com/embed/${course.video.youtubeId}`}
                title={course.video.title}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </section>
      )}

      {/* Body */}
      <section className="bg-ink-50">
        <div className="max-w-4xl mx-auto px-4 py-14 space-y-14">
          {/* Overview */}
          <div>
            <SectionHead kicker="Overview" title={`What is ${course.name}?`} />
            <p className="text-ink-700 text-[1.05rem] leading-[1.75] max-w-read">{course.overview}</p>
          </div>

          {/* Salary */}
          <div>
            <SectionHead kicker="Money" title="What you can earn" />
            {/* Chart only with two or more stages; a single fresher range is shown in the cards. */}
            {course.salaryProgression && course.salaryProgression.length > 1 && (
              <div className="mb-5">
                <RangeBars
                  rows={course.salaryProgression}
                  caption="How pay typically grows in India (₹ lakh per annum). Indicative ranges, not guarantees."
                  ariaLabel={`${course.name} salary progression in India: ${course.salaryProgression.map((p) => `${p.label}, rupees ${p.low} to ${p.high} lakh`).join('; ')}`}
                />
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-card border border-ink-200 bg-cream p-5 shadow-card">
                <div className="text-xs text-ink-500">Starting · India</div>
                <div className="font-serif text-2xl text-ink-900 mt-1">{shortSalary(course.startingSalaryIndia)}</div>
                <p className="text-sm text-ink-600 mt-2 leading-relaxed">{course.startingSalaryIndia}</p>
              </div>
              <div className="rounded-card border border-ink-200 bg-cream p-5 shadow-card">
                <div className="text-xs text-ink-500">All experience · India</div>
                <div className="font-serif text-2xl text-ink-900 mt-1">{shortSalary(course.experiencedSalaryIndia)}</div>
                <p className="text-sm text-ink-600 mt-2 leading-relaxed">{course.experiencedSalaryIndia}</p>
              </div>
            </div>
            <div className="mt-3 rounded-card border border-ink-200 bg-cream p-5 shadow-card">
              <div className="text-xs text-ink-500">Working abroad</div>
              <p className="text-sm text-ink-700 mt-1.5 leading-relaxed">{course.salaryAbroad}</p>
            </div>
            <p className="text-[11px] text-ink-500 mt-2">Indicative 2026 ranges; real pay varies by firm, city, and performance.</p>
          </div>

          {/* Market demand */}
          <div>
            <SectionHead kicker="Demand" title="Current market" />
            <p className="text-ink-700 text-[1.05rem] leading-[1.75] max-w-read">{course.marketDemand}</p>
          </div>

          {/* Where you work */}
          <div>
            <SectionHead kicker="Roles" title="Where you can work" />
            <div className="grid gap-2 sm:grid-cols-2">
              {course.whereYouWork.map((w) => (
                <div key={w} className="flex gap-2.5 rounded-card border border-ink-200/80 bg-cream px-4 py-3.5 shadow-soft">
                  <span className="text-accent-500 mt-0.5 shrink-0" aria-hidden="true">→</span>
                  <span className="text-sm text-ink-700">{w}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Career growth */}
          <div>
            <SectionHead kicker="Growth" title="Career path" />
            <p className="text-ink-700 text-[1.05rem] leading-[1.75] max-w-read">{course.careerGrowth}</p>
          </div>

          {/* India recognition callout */}
          <div className="rounded-card bg-gradient-to-r from-accent-50 to-cream px-5 py-5 shadow-soft border border-accent-100 border-l-4 border-l-accent-500">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-accent-600 mb-1.5">Recognition in India — read this</h3>
            <p className="text-sm text-ink-700 leading-relaxed">{course.indiaRecognition}</p>
          </div>

          {/* Pros / Cons */}
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="rounded-card border border-ink-200 bg-cream p-5 shadow-card">
              <h3 className="font-serif text-lg text-ink-900 mb-3">The upside</h3>
              <ul className="space-y-2">
                {course.pros.map((p) => (
                  <li key={p} className="flex gap-2 text-sm text-ink-700">
                    <span className="text-econ-500" aria-hidden="true">✓</span><span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-card border border-ink-200 bg-cream p-5 shadow-card">
              <h3 className="font-serif text-lg text-ink-900 mb-3">The trade-offs</h3>
              <ul className="space-y-2">
                {course.cons.map((c) => (
                  <li key={c} className="flex gap-2 text-sm text-ink-700">
                    <span className="text-biz-500" aria-hidden="true">!</span><span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Who should do it */}
          <div>
            <SectionHead kicker="Fit" title="Who should do it?" />
            <p className="text-ink-700 text-[1.05rem] leading-[1.75] max-w-read">{course.whoShouldDoIt}</p>
          </div>

          {/* At a glance */}
          <div className="rounded-card border border-ink-200 bg-cream p-5 shadow-card">
            <h3 className="font-serif text-lg text-ink-900 mb-3">At a glance</h3>
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div><dt className="text-ink-500 text-xs">Awarding body</dt><dd className="text-ink-800">{course.awardingBody}</dd></div>
              <div><dt className="text-ink-500 text-xs">Eligibility</dt><dd className="text-ink-800">{course.eligibility}</dd></div>
              <div><dt className="text-ink-500 text-xs">Duration</dt><dd className="text-ink-800">{course.duration}</dd></div>
              <div><dt className="text-ink-500 text-xs">Exam structure</dt><dd className="text-ink-800">{course.structure}</dd></div>
              <div className="sm:col-span-2"><dt className="text-ink-500 text-xs">Approximate cost</dt><dd className="text-ink-800">{course.costInr}</dd></div>
            </dl>
          </div>

          <p className="text-xs text-ink-500">Fees and salaries on this page were checked on 25 October 2026. Rupee conversions for GBP and USD use ECB reference rates from 23 October 2026 (about ₹126.92 per GBP and ₹94.94 per USD).</p>

          {/* Sources */}
          {course.sources && course.sources.length > 0 && (
            <details className="text-xs text-ink-500">
              <summary className="cursor-pointer hover:text-ink-700">Sources</summary>
              <ul className="mt-2 space-y-1 break-all">
                {course.sources.map((s) => (
                  <li key={s}><a href={s} target="_blank" rel="noopener noreferrer" className="text-accent-600 hover:underline">{s}</a></li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </section>

      {/* Compare others */}
      <section className="bg-cream border-y border-ink-200">
        <div className="max-w-4xl mx-auto px-4 py-10">
          <h2 className="font-serif text-xl text-ink-900 mb-4">Compare other courses</h2>
          <div className="flex flex-wrap gap-2">
            {others.map((c) => (
              <Link
                key={c.slug}
                to={`/careers/courses/${c.slug}/`}
                className="px-4 py-2 rounded-full border border-ink-200/80 bg-cream text-sm text-ink-700 shadow-soft hover:border-accent-500/60 hover:bg-accent-50 hover:text-accent-700 transition-colors duration-150"
              >
                {c.name} →
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="aces-band-night bg-night-700">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <h2 className="font-serif text-2xl text-white tracking-tight">Build the foundation first</h2>
          <p className="text-ink-100/80 mt-3 text-sm max-w-md mx-auto leading-relaxed">
            Every one of these starts with strong Class 11 &amp; 12 commerce. Get the basics
            rock-solid — free, no login.
          </p>
          <Link
            to="/"
            className="aces-btn-primary mt-7"
          >
            Start learning — free →
          </Link>
        </div>
      </section>
    </div>
  );
}
